// ----------------------------------------------------------------------------
// JotForm — Inline Confirmation (no thank-you-page redirect)
// ----------------------------------------------------------------------------
//
// JotForm "Source Code" embeds (pasted into a Shopify page — currently the
// Warranty and Corporate Orders pages) submit natively to JotForm and then
// follow a 302 redirect to a thank-you page, navigating away from the store.
//
// This module keeps the visitor on the page instead: it routes each
// `.jotform-form` submission into a hidden iframe (so the redirect happens
// off-screen, not at the top level), then hides the form and reveals an inline
// thank-you message once the submission completes.
//
// It is a no-op on pages without a `.jotform-form`, so it is safe to load
// globally. JotForm's own client-side validation still runs first — if the
// form is invalid the native submit never fires and nothing here triggers.
//
// Caveat: this assumes the embed submits natively (which is what produces the
// redirect we're replacing). If a form is ever reconfigured to submit purely
// via JotForm's own AJAX, the iframe `load` signal won't fire and the inline
// message won't show — re-test if the embed type changes.
// ----------------------------------------------------------------------------

// -------------------------------------
// Config
// -------------------------------------

var FORM_SELECTOR = 'form.jotform-form';
var DEFAULTS = {
  heading: 'Thank you',
  message: 'Your submission has been received. We’ll be in touch shortly.'
};

// -------------------------------------
// Helpers
// -------------------------------------

/**
 * Build the inline thank-you element shown in place of the form.
 * Copy can be overridden per-form via `data-thankyou-heading` /
 * `data-thankyou-message` attributes on the `<form>`.
 * @param {HTMLFormElement} form
 * @returns {HTMLElement}
 */
function buildThankYou(form) {
  var heading = form.dataset.thankyouHeading || DEFAULTS.heading;
  var message = form.dataset.thankyouMessage || DEFAULTS.message;
  var wrap = document.createElement('div');
  wrap.className = 'jotform-thank-you';
  wrap.setAttribute('role', 'status');
  wrap.setAttribute('aria-live', 'polite');
  wrap.setAttribute('tabindex', '-1');
  var title = document.createElement('h2');
  title.className = 'jotform-thank-you__heading';
  title.textContent = heading;
  var body = document.createElement('p');
  body.className = 'jotform-thank-you__message';
  body.textContent = message;
  wrap.append(title, body);
  return wrap;
}

/**
 * Swap the form out for the thank-you message and move focus to it.
 * @param {HTMLFormElement} form
 */
function showThankYou(form) {
  if (form.dataset.thankyouShown === 'true') return;
  form.dataset.thankyouShown = 'true';
  var thankYou = buildThankYou(form);
  form.insertAdjacentElement('beforebegin', thankYou);
  form.hidden = true;
  thankYou.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  });
  thankYou.focus({
    preventScroll: true
  });
}

/**
 * Route a single form's submission into a hidden iframe and wire up the
 * inline confirmation on completion.
 * @param {HTMLFormElement} form
 * @param {number} index
 */
function enhanceForm(form, index) {
  if (form.dataset.inlineConfirmation === 'true') return;
  form.dataset.inlineConfirmation = 'true';
  var frameName = "jotform-sink-".concat(form.id || index);
  var frame = document.createElement('iframe');
  frame.name = frameName;
  frame.title = 'Form submission target';
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.display = 'none';
  document.body.appendChild(frame);

  // Only react to iframe loads triggered by an actual submission, not the
  // initial about:blank load that some browsers fire on insertion.
  var armed = false;

  // Capture phase: point the form at the hidden iframe before JotForm's own
  // (bubble-phase) validation/submit handlers run. If validation cancels the
  // submit, the target is set harmlessly and nothing navigates.
  form.addEventListener('submit', () => {
    form.target = frameName;
    armed = true;
  }, true);
  frame.addEventListener('load', () => {
    if (!armed) return;
    armed = false;
    showThankYou(form);
  });
}

// -------------------------------------
// Init
// -------------------------------------

function init() {
  var forms = document.querySelectorAll(FORM_SELECTOR);
  forms.forEach(enhanceForm);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
export default init;